/**
 * @module experience/product-selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { PRODUCT_SELECTOR_CONSTANTS } from '@experience/shop/product-selector/constants/product-selector.constants';
import { ProductSelectorContentData } from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { BrowseHardgoodsService } from '@experience/shop/hardgoods/services/browse-hardgoods.service';
import { HardgoodsDeviceDetails } from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
import { HARDGOODS_INFINITE_SCROLL_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { BrowseDeviceModel } from '@experience/shop/hardgoods/models/browse-device.model';
import { APIError } from '@support/api/common/error.interface';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tmo-device-section',
  templateUrl: './device-section.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../../style-sheets/components/dropdown.component.scss',
    '../../../../../../style-sheets/components/input-radio.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
    '../../../../../../style-sheets/components/html-accordion.component.scss',
    '../../../../../../style-sheets/components/gallery.component.scss',
    '../../../../../../style-sheets/components/html-blade-links.component.scss',
  ],
})
export class DeviceSectionComponent implements OnInit, OnDestroy {
  public isBringYourOwnDevice: boolean;

  public constant: Object;
  public showSpinner: boolean;

  public imagesData: BrowseDeviceModel[];
  /**
   * Holds boolean if user starts with BYOS scenario
   */
  @Input() public isByosFlow: boolean;
  /**
   * Unsubscribes from all the subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  @Input() public deviceSectionAEMContent: ProductSelectorContentData;

  @Input() public noPlanNeeded: boolean;
  /**
   * Holds value of currently selected Rdaio btn
   */
  private selectedRadioBtn: string;
  /**
   * Holds boolean to hide radio buttons
   */
  public hideRadioButtons: boolean;
  /**
   * Holds Api error
   */
  public apiErrorMessage: APIError[];
  constructor(
    private browseHardgoodsService: BrowseHardgoodsService,
    private router: Router,
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService
  ) {
    this.ngUnsubscribe = new Subject<void>();
  }

  public ngOnInit(): void {
    this.imagesData = [];
    this.selectedRadioBtn = PRODUCT_SELECTOR_CONSTANTS.SHOP_FOR_A_DEVICE;
    this.updateSelectedRadio();
    this.constant = PRODUCT_SELECTOR_CONSTANTS;
    if (this.selectedRadioBtn === PRODUCT_SELECTOR_CONSTANTS.SHOP_FOR_A_DEVICE) {
      this.getDeviceCarousel();
    }
  }

  public ngOnDestroy(): void {
    this.isBringYourOwnDevice = false;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Radio Btn click handler.
   * @param { radioBtnName } Clicked Radio button name
   */
  public handleRadioClickEvent(isBYOD: boolean): void {
    this.isBringYourOwnDevice = isBYOD;
    this.getDeviceCarousel();
  }

  /**
   * Update selectedRadioBtn based on isBringYourOwnDevice's value
   */
  private updateSelectedRadio(): void {
    // if (SETTINGS.CHANNEL.toLowerCase() !== CHANNELS.RETAIL){
    this.isBringYourOwnDevice = this.hideRadioButtons = this.isByosFlow;
    this.selectedRadioBtn = this.isByosFlow
      ? PRODUCT_SELECTOR_CONSTANTS.BRING_YOUR_OWN_DEVICE
      : PRODUCT_SELECTOR_CONSTANTS.SHOP_FOR_A_DEVICE;
    // }
  }

  /**
   * Function to navigate to BYOD Screen and saving the previous state to return
   */
  public navigateToBringYourOwnDevice(): void {
    this.stateStoreUtilsService.setPreviousUrl(this.router.url);
    this.router.navigate([PRODUCT_SELECTOR_CONSTANTS.BRING_YOUR_OWN_DEVICE_REQUEST_URL], {
      queryParams: { allowEdit: true },
    });
  }

  public getDeviceCarousel(): void {
    if (this.imagesData.length < 1) {
      this.showSpinner = true;
      // tslint:disable no-any
      this.browseHardgoodsService
        .loadHardgoodsData('browse-phones', HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER)
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe(
          (response: HardgoodsDeviceDetails) => {
            this.showSpinner = false;
            if (this.objectService.get(response, 'productModels.length', 0) > 0) {
              response.productModels.forEach((product: BrowseDeviceModel) => {
                this.imagesData.push(product);
              });
            }
          },
          (error: APIError[]) => {
            this.apiErrorMessage = error;
          }
        );
    }
  }

  public moveToRoute(event: string): void {
    this.stateStoreUtilsService.setPreviousUrl(this.router.url);
    this.router.navigate([event]);
  }
}
